import { resolveActiveNavLink } from "./navActiveLink"
import { incrementPageVisit, type PageVisitScope } from "./pageVisits"

export type QuickShortcutNavigationItem = {
 id?: string
 label: string
 link?: string
 icon?: string
 type?: "section"
 subItems?: QuickShortcutNavigationItem[]
}

const PRIORITY_IDS = [
 "nav-pos-screen",
 "nav-sales",
 "nav-purchases-list",
 "nav-products",
 "nav-customers-list",
 "nav-inventory-main",
 "nav-inventory-warehouses",
 "nav-expenses",
 "nav-financial-accounts",
 "nav-shifts",
 "nav-driver-settlements",
 "nav-rep-profit-loss",
 "nav-reports-hub",
]

const MAX_SHORTCUTS = 13

function flattenNavigationItems(
 items: QuickShortcutNavigationItem[],
): QuickShortcutNavigationItem[] {
 return items.flatMap((item) => {
 if (item.type === "section") return []

 const currentItem = item.link ? [item] : []
 return [...currentItem, ...(item.subItems ? flattenNavigationItems(item.subItems) : [])]
 })
}

export function flattenQuickShortcutItems(
 items: QuickShortcutNavigationItem[],
): QuickShortcutNavigationItem[] {
 return flattenNavigationItems(items)
}

export function buildQuickShortcuts(
 items: QuickShortcutNavigationItem[],
): QuickShortcutNavigationItem[] {
 const availableItems = flattenNavigationItems(items)
 const byId = new Map(
 availableItems
 .filter((item) => item.id)
 .map((item) => [item.id!, item]),
 )
 const prioritizedItems = PRIORITY_IDS.flatMap((id) => {
 const item = byId.get(id)
 return item ? [item] : []
 })

 if (prioritizedItems.length > 0) {
 return prioritizedItems.slice(0, MAX_SHORTCUTS)
 }

 return availableItems
 .filter((item) => item.link && item.link !== "/")
 .slice(0, MAX_SHORTCUTS)
}

export const MAX_MOST_VISITED_SHORTCUTS = 4

export function buildMostVisitedShortcuts(
 items: QuickShortcutNavigationItem[],
 rankedLinks: string[],
 limit = MAX_MOST_VISITED_SHORTCUTS,
): QuickShortcutNavigationItem[] {
 const availableItems = flattenNavigationItems(items).filter((item) => item.link && item.link !== '/')
 const byLink = new Map(availableItems.map((item) => [item.link!, item]))
 const seen = new Set<string>()
 const result: QuickShortcutNavigationItem[] = []

 for (const link of rankedLinks) {
  const item = byLink.get(link)
  if (!item) continue
  const key = item.id ?? item.link ?? ''
  if (!key || seen.has(key)) continue
  seen.add(key)
  result.push(item)
  if (result.length >= limit) break
 }

 return result
}

export function recordShortcutVisit(
 pathname: string,
 search: string,
 items: QuickShortcutNavigationItem[],
 scope?: PageVisitScope | null,
) {
 if (scope === null) return
 const links = flattenNavigationItems(items)
  .map((item) => item.link)
  .filter((link): link is string => Boolean(link))
 const match = resolveActiveNavLink(links, pathname, search)
 if (!match) return
 incrementPageVisit(match, Date.now(), scope)
}
